import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useRouter } from 'next/dist/client/router';
import { toast } from 'react-toastify';

import {
  AuthContextLoginParams,
  LoggedInUserInfo,
  UserProfile,
} from '../../models';
import { MessageService } from '../../utilities';
import { STORAGE_KEYS, appConfig } from '../../constants';
import {
  changeAvatarApi,
  getLoggedInUserInfoApi,
  isUserAddedBySchool,
  updateUserInfoApi,
} from '../../services/user/user.api';
import { getPaymentPlansApi } from '../../services/payment/payment.api';
import { useLoginViaParams } from '../../hooks/useLoginViaParams';
import { useSafePush } from '../../hooks/useSafePush';
import { checkDomainIsAdmin } from '../../helpers/checkDomainIfAdmin';
import {
  ROUTES,
  WHITELISTED_PAGES_FOR_NON_SUBSCRIBED,
  AuthProviderProps,
  IAuthContext,
} from './AuthContent.type';

dayjs.extend(relativeTime);

const AuthContext = createContext<IAuthContext>({
  login: () => Promise.reject(),
  logout: () => Promise.reject(),
  isAuthenticated: () => false,
  setRememberMe: () => null,
  setUser: () => null,
  isSubscribed: () => null,
  loading: true,
  fetchUserInfo: () => Promise.reject(),
  role: {
    isSuperAdmin: false,
    isSchoolAdmin: false,
    isTeacher: false,
    isStudent: false,
  },
});

//Check Environment

function AuthProvider({
  children,
  homeRoute,
  loginRoute,
  app,
}: AuthProviderProps) {
  const [accessToken, setAccessToken] = useState<string>();
  const [user, setUser] = useState<LoggedInUserInfo>();
  const [loading, setLoading] = useState(true);
  const [rememberMe, setRememberMe] = useState<boolean>();
  const router = useRouter();
  const { safePush } = useSafePush();
  const authWindow = useMemo(
    () => typeof window !== 'undefined' && window?.opener,
    []
  );
  const loginViaParams = useLoginViaParams();

  const afterLogin = (loginParams: AuthContextLoginParams) => {
    // Can only be triggered from the child window (platform)
    if (!authWindow) {
      return;
    }
    /**
     * Send login params to parent window (admin)
     * Admin listens to this event and handles the login
     * using the useAuthWindow hook
     */
    MessageService.requestLoginFromChild(loginParams);
    return window.close();
  };

  const handleStudentRedirect = async (
    isOnSignup: boolean,
    redirect?: string
  ) => {
    setLoading(true);
    try {
      const { isSchoolStudent } = await isUserAddedBySchool();
      if (isSchoolStudent) {
        const callbackUrl = router.query?.['callbackUrl'] || '';
        const redirectUrl = isOnSignup
          ? ROUTES.signup_welcomeBack
          : callbackUrl
          ? callbackUrl
          : redirect;
        const _url = typeof redirectUrl === 'string' ? redirectUrl : homeRoute;
        safePush(_url);
        return;
      } else {
        const url = isOnSignup ? ROUTES.signup_trial : redirect;
        const _url = typeof url === 'string' ? url : homeRoute;
        safePush(_url);
        return;
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const login: IAuthContext['login'] = async (
    { accessToken, refreshToken, rememberMe, role },
    redirect,
    isOnSignup = false
  ) => {
    setAccessToken(accessToken);
    if (role === appConfig.role.STUDENT) {
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
      localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, `${rememberMe}`);
      localStorage.setItem(STORAGE_KEYS.USER_ROLE, role || '');
    } else {
      localStorage.setItem(STORAGE_KEYS.ADM_ACCESS_TOKEN, accessToken);
      localStorage.setItem(STORAGE_KEYS.ADM_REFRESH_TOKEN, refreshToken);
      localStorage.setItem(STORAGE_KEYS.ADM_REMEMBER_ME, `${rememberMe}`);
      localStorage.setItem(STORAGE_KEYS.ADM_USER_ROLE, role || '');
    }
    localStorage.setItem(STORAGE_KEYS.VERIFY_PROCESS, 'false');
    // authWindow is true when the login is triggered from the admin
    if (authWindow) {
      if (role === appConfig.role.STUDENT) {
        MessageService.requestLoginFromChild({
          accessToken,
          refreshToken,
          rememberMe,
          role,
        });
        handleStudentRedirect(isOnSignup, redirect);
      } else if (window?.parent?.opener === null) {
        loginViaParams({
          accessToken,
          refreshToken,
          role,
          rememberMe,
        });
        window.name = '';
      } else {
        afterLogin({
          accessToken,
          refreshToken,
          rememberMe,
          role,
        });
      }
    } else if (app === 'PLATFORM' && role !== appConfig.role.STUDENT) {
      loginViaParams({
        accessToken,
        refreshToken,
        role,
        rememberMe,
      });
      return;
    } else if (role === appConfig.role.STUDENT) {
      handleStudentRedirect(isOnSignup, redirect);
    } else if (redirect) {
      safePush(typeof redirect === 'string' ? redirect : homeRoute);
    }
  };

  const fetchUserInfo = async () => {
    try {
      const user = await getLoggedInUserInfoApi();
      const age = Math.floor(dayjs().diff(dayjs(user.birthDate), 'year'));
      setUser({ ...user, age });
    } catch (error: any) {
      toast.error(error?.data?.message);
      if (
        error?.data?.message?.includes('expired') ||
        error?.data?.message?.includes('is no longer active') ||
        error?.data?.message?.includes('No subscription') ||
        error?.data?.message?.includes('No active subscription')
      ) {
        safePush(ROUTES.signup_subscription_paywall);
      }
    }
  };

  const logout: IAuthContext['logout'] = useCallback(
    async (redirect = loginRoute) => {
      setLoading(true);
      try {
        if (checkDomainIsAdmin()) {
          localStorage.removeItem(STORAGE_KEYS.ADM_ACCESS_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.ADM_REFRESH_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.ADM_REMEMBER_ME);
          localStorage.removeItem(STORAGE_KEYS.ADM_USER_ROLE);
          localStorage.removeItem(STORAGE_KEYS.CLEVER_CONNECTION_STATUS);
          localStorage.removeItem(STORAGE_KEYS.CLEVER_JOB_ID);
          localStorage.removeItem(STORAGE_KEYS.SHIBBOLETH_CONNECTION_STATUS);
        } else {
          localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
          localStorage.removeItem(STORAGE_KEYS.USER_ROLE);
        }
        localStorage.removeItem(STORAGE_KEYS.VERIFY_PROCESS);
        localStorage.removeItem(STORAGE_KEYS.VERIFY_ID);
        localStorage.removeItem(STORAGE_KEYS.SIGN_IN_PROCESS);
        if (redirect) {
          safePush(typeof redirect === 'string' ? redirect : homeRoute);
        }

        setAccessToken(undefined);
        setUser(undefined);
      } catch (error: unknown) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [checkDomainIsAdmin]
  );

  const refreshToken = useCallback(
    (tokens: { accessToken: string; refreshToken: string }) =>
      login({ ...tokens, rememberMe }),
    [rememberMe]
  );

  const removeTokens = useCallback(() => {
    let rememberMeToken;

    if (checkDomainIsAdmin()) {
      rememberMeToken = localStorage.getItem(STORAGE_KEYS.ADM_REMEMBER_ME);
      if (!rememberMeToken) {
        localStorage.removeItem(STORAGE_KEYS.ADM_ACCESS_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.ADM_REFRESH_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.ADM_USER_ROLE);
        localStorage.removeItem(STORAGE_KEYS.ADM_REMEMBER_ME);
      }
    } else {
      rememberMeToken = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME);
      if (!rememberMeToken) {
        localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.USER_ROLE);
        localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
      }
    }
  }, [rememberMe]);

  const isSubscribed = useCallback(async () => {
    if (accessToken) {
      try {
        if (!WHITELISTED_PAGES_FOR_NON_SUBSCRIBED.includes(router.pathname)) {
          const res = await getPaymentPlansApi();
          const { isSchoolStudent } = await isUserAddedBySchool();
          const activePlan = res.find((el) => el.active);
          if (!activePlan?.active && !isSchoolStudent) {
            safePush(ROUTES.expired_subscription);
          }
        }
      } catch (err) {
        console.log(err);
      }
    }
  }, [accessToken, router.pathname]);

  const isAuthenticated = useCallback(() => {
    if (checkDomainIsAdmin()) {
      return !!localStorage.getItem(STORAGE_KEYS.ADM_ACCESS_TOKEN);
    }
    return !!localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  }, [accessToken]);

  function onSetRememberMe(rememberMe: boolean) {
    setRememberMe(rememberMe);
    if (checkDomainIsAdmin()) {
      localStorage.setItem(STORAGE_KEYS.ADM_REMEMBER_ME, `${rememberMe}`);
    } else {
      localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, `${rememberMe}`);
    }
  }

  async function updateUserInfo(userProfile: UserProfile, cb?: () => void) {
    const updatedProfile = await updateUserInfoApi(userProfile);
    setUser(
      (user) =>
        ({
          ...user,
          ...updatedProfile,
        } as LoggedInUserInfo)
    );
    cb?.();

    return updatedProfile;
  }

  async function updateAvatar(avatar: File, cb?: () => void) {
    const formData = new FormData();
    formData.append('image', avatar);
    const updatedProfile = await changeAvatarApi(formData);
    setUser(
      (user) =>
        ({
          ...user,
          ...updatedProfile,
        } as LoggedInUserInfo)
    );
    cb?.();
    return updatedProfile;
  }

  useEffect(() => {
    let token;
    let rememberMeCookie;
    if (checkDomainIsAdmin()) {
      token = localStorage.getItem(STORAGE_KEYS.ADM_ACCESS_TOKEN);
      rememberMeCookie = localStorage.getItem(STORAGE_KEYS.ADM_REMEMBER_ME);
    } else {
      token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      rememberMeCookie = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME);
    }
    if (token) {
      setAccessToken(token);
      setRememberMe(!!rememberMeCookie);
      fetchUserInfo();
    } else if (!isAuthenticated()) {
      if (typeof window !== 'undefined') {
        const path = window.location.pathname;
        const encodedPath = encodeURIComponent(path);
        if (!path.includes('auth')) {
          safePush(loginRoute + `?callbackUrl=${encodedPath}`);
        }
      }
    }
    setLoading(false);
  }, [router.pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.onbeforeunload = removeTokens;

    return () => {
      window.removeEventListener('beforeunload', removeTokens);
    };
  }, [removeTokens]);

  useEffect(() => {
    MessageService.addRequestLogoutEventListener(logout);
    MessageService.addRequestRefreshTokensEventListener(refreshToken);

    return () => {
      MessageService.removeRequestLogoutEventListener();
      MessageService.removeRequestRefreshTokensEventListener();
    };
  }, [refreshToken]);

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        user,
        setUser,
        login,
        logout,
        isAuthenticated,
        isSubscribed,
        loading: loading,
        setRememberMe: onSetRememberMe,
        updateUserInfo,
        updateAvatar,
        fetchUserInfo,
        role: {
          isSuperAdmin: (user && user?.role === appConfig.role.ADMIN) || false,
          isSchoolAdmin:
            (user && user?.role === appConfig.role.SCHOOL) || false,
          isTeacher: (user && user?.role === appConfig.role.TEACHER) || false,
          isStudent: (user && user?.role === appConfig.role.STUDENT) || false,
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
